import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Container, Dimmer, Image, Loader, Segment } from 'semantic-ui-react';

import WSA from '../WSA';
import { getTitlePrefix } from '../config/env';
import { routes } from '../config/routes';
import { changeLanguage } from '../i18n';

// need this when we handle sign up
import { userSaveSignUpData } from '../redux/actions/userActions';

// Import components
import { useDispatch } from 'react-redux';
import wsLogo from '../assets/images/wonderschool/logo-base.png';
import { logError } from '../rollbar';

const TIMEOUT = 10000; // 10 seconds
enum SignInStatus {
  None,
  Started,
  Finished,
}
const AuthReturn = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [didSdkSignin, setDidSdkSignin] = useState<SignInStatus>(SignInStatus.None);
  const isSignupRef = useRef<boolean>(false);
  const returnStateRef = useRef<any>(null);

  const { isReady, isAuthenticated } = WSA.Components.useWSAuth();

  useEffect(() => {
    async function completeSignIn() {
      if (isReady && didSdkSignin === SignInStatus.None) {
        // only try sign in once
        setDidSdkSignin(SignInStatus.Started); // intermediate truthy value
        try {
          // sdk sign in
          const removeHashAfter = true;

          const result = await WSA.Auth.completeSignin(removeHashAfter);
          if (!result) {
            throw new Error('CompleteSignin Result was empty');
          }
          const { user, isSignup, selectedLanguage, returnState } = result;
          isSignupRef.current = !!isSignup; // save for later use in other loops
          returnStateRef.current = returnState; // save returnState for later use

          const signupData = isSignup ? result.signupData : await WSA.Signup.getSavedSignupData(user.user);
          if (selectedLanguage && selectedLanguage !== i18n.language) {
            changeLanguage(selectedLanguage);
          }
          // save signup data for later
          dispatch(userSaveSignUpData(signupData));

          // mark signin complete
          setDidSdkSignin(SignInStatus.Finished);
        } catch (err: any) {
          logError(`Signin Completion Failed: ${err?.message}`, err, { err, isReady, didSdkSignin });
          navigate(routes.authError, { replace: true });
        }
      }

      // need a separate loop to wait for event triggers to update
      if (didSdkSignin === SignInStatus.Finished && isReady && isAuthenticated) {
        // Check for redirect parameter from returnState
        const redirectPath = returnStateRef.current?.redirectTo;

        if (isSignupRef.current) {
          // For signups, still go to verify but preserve redirect for after verification
          navigate(routes.verify, {
            state: redirectPath ? { redirectTo: redirectPath } : undefined,
            replace: true,
          });
        } else {
          // For existing users, redirect to original page or home
          const targetPath = redirectPath && redirectPath !== '/' ? redirectPath : routes.home;
          navigate(targetPath, { replace: true });
        }
      }
    }
    completeSignIn();
  }, [isReady, isAuthenticated, navigate, dispatch, i18n.language, didSdkSignin]);

  // taking too long, give up and go to error page
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(routes.authError, { replace: true });
    }, TIMEOUT);
    return () => clearTimeout(timer);
  }, [navigate]);

  const renderPageHead = () => (
    <Helmet>
      <title>
        {getTitlePrefix()}
        {t('navigation.redirect.browserTitle')}
      </title>
      <body className="auth auth-return" />
    </Helmet>
  );

  return (
    <Container style={{ width: 500 }}>
      {renderPageHead()}

      <Segment basic>
        <Image src={wsLogo} size="medium" alt={t('navigation.redirect.logoAlt')} centered />
      </Segment>

      <Dimmer page active inverted>
        <Loader inline="centered">{t('navigation.redirect.status')}</Loader>
      </Dimmer>
    </Container>
  );
};

export default AuthReturn;
